import styled from "@emotion/styled"
import { Facebook, Instagram, Twitter } from "@mui/icons-material"
import BusinessIcon from "@mui/icons-material/Business"
import LanguageIcon from "@mui/icons-material/Language"
import MailOutlineIcon from "@mui/icons-material/MailOutline"
import RoomIcon from "@mui/icons-material/Room"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"
import { Link, Typography } from "@mui/material"
import * as api from "api"
import PhoneIcon from "common/icons/PhoneIcon"
import { memo } from "react"
import Shared from "theme/shared"

const Styled = {
  Wrapper: styled.div`
    padding: 0 15px 15px;
  `,
  Property: styled(Typography)<{ component?: string }>`
    font-size: 0.75rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 0.5em;

    ${(p) => p.theme.breakpoints.up("md")} {
      font-size: 1rem;
    }

    svg {
      font-size: 1.2em;
      color: ${(p) => p.theme.colors.bluishGrey};

      margin-inline-end: 0.3em;
      g {
        fill: ${(p) => p.theme.colors.bluishGrey};
      }
    }
    span {
      width: 100%;
    }
  `,
  Address: styled.span`
    width: auto !important;
  `,
  Link: styled(Link)`
    color: ${(p) => p.theme.colors.marine} !important;
    text-decoration: underline;
    display: flex;
  `,
}

type DetailsProps = {
  serviceCenter: api.SingleServiceCenter & api.ServiceCenter
}

const Details = ({ serviceCenter }: DetailsProps) => {
  const mapLink = `https://www.google.com/maps/search/?api=1&query=${serviceCenter.location?.latitude},${serviceCenter.location?.longitude}&hl=iw`
  return (
    <Styled.Wrapper>
      <Styled.Property component="div">
        <RoomIcon />
        <Styled.Address>{serviceCenter.address} &nbsp;</Styled.Address>
        <Shared.Mobile>
          <b>
            <Styled.Link
              href={serviceCenter.wazeLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              ניווט למרכז שירות
            </Styled.Link>
          </b>
        </Shared.Mobile>
        <Shared.Desktop>
          <b>
            <Styled.Link
              href={mapLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              הצג במפה
            </Styled.Link>
          </b>
        </Shared.Desktop>
      </Styled.Property>

      <Styled.Property>
        <span>שעות פעילות:</span>
        {Object.entries(serviceCenter.schedule).map(([time, days], i) => (
          <span key={i}>
            {[...days].join(",")}: {time}
          </span>
        ))}
      </Styled.Property>

      <Styled.Property title="טלפון">
        <PhoneIcon />
        <Styled.Link href={`tel:${serviceCenter.phone}`}>
          {serviceCenter.phone}
        </Styled.Link>
      </Styled.Property>

      {serviceCenter.whatsappLink && (
        <Styled.Property title="וואטסאפ">
          <WhatsAppIcon />
          <Styled.Link
            href={serviceCenter.whatsappLink}
            rel="noopener noreferrer"
            target="_blank"
          >
            {serviceCenter.whatsapp}
          </Styled.Link>
        </Styled.Property>
      )}

      <Styled.Property title="אימייל">
        <MailOutlineIcon />
        <Styled.Link href={`mailto:${serviceCenter.email}`}>
          {serviceCenter.email}
        </Styled.Link>
      </Styled.Property>

      {serviceCenter.fax && (
        <Styled.Property title="פקס">
          <BusinessIcon />
          {serviceCenter.fax}
        </Styled.Property>
      )}
      <Styled.Property title="בקרו אותנו">
        בקרו אותנו&nbsp;
        {serviceCenter.siteName && (
          <Styled.Link
            title="באתר"
            href={serviceCenter.siteName}
            rel="noopener noreferrer"
            target="_blank"
          >
            <LanguageIcon />
          </Styled.Link>
        )}
        {serviceCenter.facebook && (
          <Styled.Link
            title="בפייסבוק"
            href={serviceCenter.facebook}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Facebook />
          </Styled.Link>
        )}
        {serviceCenter.twitter && (
          <Styled.Link
            title="בטוויטר"
            href={serviceCenter.twitter}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Twitter />
          </Styled.Link>
        )}
        {serviceCenter.instegram && (
          <Styled.Link
            title="באינסטגרם"
            href={serviceCenter.instegram}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Instagram />
          </Styled.Link>
        )}
      </Styled.Property>
    </Styled.Wrapper>
  )
}

export default memo(Details)
